import { Controller } from "@hotwired/stimulus"

// Three-stage sizing for the narrow check pane: collapsed, half, full.
//
// Independent of the empty-state auto-shrink (that one is order-driven and
// purely server-side — see .pu-pos-check-aside--empty in pos.css); this is a
// per-device choice, persisted the same way ops-theme is: the class swaps
// instantly here, and a cookie carries the choice into the next full request
// so the pane renders at the chosen size on load instead of flashing at the
// default first. PosController::CHECK_PANE_SIZES reads it back.
//
// This controller only ever swaps one class on itself. It never touches
// check_header or the footer (totals + PAY) — they're siblings of the element
// collapsing hides (.pu-pos-check-rounds), not descendants of anything this
// controller reaches — which is what keeps "collapsed" meaning "summary
// strip" rather than risking it meaning "gone". Which handle is offered at
// each stage is CSS keyed off the same class, so there is nothing here to
// keep in sync with it.
const SIZES = ["collapsed", "half", "full"]
const CLASS_FOR = {
  collapsed: "pu-pos-check-aside--collapsed",
  half: null, // the stylesheet's default; no class of its own
  full: "pu-pos-check-aside--full"
}

export default class extends Controller {
  grow() {
    this.#step(1)
  }

  shrink() {
    this.#step(-1)
  }

  #step(direction) {
    const next = SIZES[this.#index() + direction]
    // No wrap-around: pressing grow at full or shrink at collapsed does
    // nothing. Both handles are hidden at their extreme anyway, so this only
    // catches a keyboard or programmatic press.
    if (!next) return

    Object.values(CLASS_FOR).forEach((klass) => {
      if (klass) this.element.classList.remove(klass)
    })
    if (CLASS_FOR[next]) this.element.classList.add(CLASS_FOR[next])

    document.cookie = `ops_pos_check_size=${next}; path=/; max-age=31536000; samesite=lax; secure`
  }

  #index() {
    const current = SIZES.find((size) => {
      const klass = CLASS_FOR[size]
      return klass && this.element.classList.contains(klass)
    })
    return SIZES.indexOf(current || "half")
  }
}
